// Sub-components used by <ProductionMarks/>.  Splitting trim / bleed /
// safety / crop / center / legend into focused helpers keeps the parent
// composition declarative and trims the parent's cyclomatic complexity.

// One geometric "Mark" — renders either a rectangle, circle, or ellipse with
// the supplied stroke style.  Used by TrimMark / BleedMark / SafetyMark.
function ShapedMark({ shape, cx, cy, w, h, expand = 0, stroke, strokeWidth, dash }) {
  const common = { fill: "none", stroke, strokeWidth, strokeDasharray: dash };
  if (shape === "circle") {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={Math.min(w, h) / 2 + expand}
        {...common}
      />
    );
  }
  if (shape === "oval") {
    return (
      <ellipse
        cx={cx}
        cy={cy}
        rx={w / 2 + expand}
        ry={h / 2 + expand}
        {...common}
      />
    );
  }
  return (
    <rect
      x={cx - w / 2 - expand}
      y={cy - h / 2 - expand}
      width={w + 2 * expand}
      height={h + 2 * expand}
      {...common}
    />
  );
}

export function BleedMark({ shape, cx, cy, w, h, bleedIn }) {
  return (
    <ShapedMark
      shape={shape}
      cx={cx}
      cy={cy}
      w={w}
      h={h}
      expand={bleedIn}
      stroke="#ef4444"
      strokeWidth="0.012"
      dash="0.08 0.06"
    />
  );
}

export function TrimMark({ shape, cx, cy, w, h }) {
  return (
    <ShapedMark
      shape={shape}
      cx={cx}
      cy={cy}
      w={w}
      h={h}
      stroke="#333F48"
      strokeWidth="0.018"
    />
  );
}

export function SafetyMark({ shape, cx, cy, w, h, safetyIn }) {
  // For circle/oval the safety ring just contracts the radius by safetyIn.
  // For rect we shrink the rect's width/height by 2*safetyIn and use expand=0.
  if (shape === "circle" || shape === "oval") {
    return (
      <ShapedMark
        shape={shape}
        cx={cx}
        cy={cy}
        w={w}
        h={h}
        expand={-safetyIn}
        stroke="#16a34a"
        strokeWidth="0.01"
        dash="0.06 0.06"
      />
    );
  }
  return (
    <ShapedMark
      shape="rect"
      cx={cx}
      cy={cy}
      w={Math.max(0, w - 2 * safetyIn)}
      h={Math.max(0, h - 2 * safetyIn)}
      expand={0}
      stroke="#16a34a"
      strokeWidth="0.01"
      dash="0.06 0.06"
    />
  );
}

export function CenterCrosshairs({ originX, originY, w, h }) {
  return (
    <g stroke="#94a3b8" strokeWidth="0.005" strokeDasharray="0.04 0.04">
      <line x1={originX} y1={originY + h / 2} x2={originX + w} y2={originY + h / 2} />
      <line x1={originX + w / 2} y1={originY} x2={originX + w / 2} y2={originY + h} />
    </g>
  );
}

export function CropMarks({ corners, tick = 0.125, gap = 0.06 }) {
  return (
    <g stroke="#0f172a" strokeWidth="0.013" strokeLinecap="square">
      {corners.map(({ x, y, isLeft, isTop, id }) => {
        const hx1 = isLeft ? x - tick - gap : x + gap;
        const hx2 = isLeft ? x - gap : x + tick + gap;
        const vy1 = isTop ? y - tick - gap : y + gap;
        const vy2 = isTop ? y - gap : y + tick + gap;
        return (
          <g key={id}>
            <line x1={hx1} y1={y} x2={hx2} y2={y} />
            <line x1={x} y1={vy1} x2={x} y2={vy2} />
          </g>
        );
      })}
    </g>
  );
}

export function LegendChip({ widthIn, heightIn, vbW, vbH }) {
  return (
    <g transform={`translate(${vbW - 1.55}, ${vbH - 0.45})`} fontFamily="ui-sans-serif, system-ui">
      <rect x="0" y="0" width="1.4" height="0.36" rx="0.04" fill="white" fillOpacity="0.92" stroke="#cbd5e1" strokeWidth="0.005" />
      <line x1="0.06" y1="0.1" x2="0.18" y2="0.1" stroke="#333F48" strokeWidth="0.018" />
      <text x="0.22" y="0.13" fontSize="0.08" fill="#0f172a">Trim</text>
      <line x1="0.06" y1="0.2" x2="0.18" y2="0.2" stroke="#ef4444" strokeWidth="0.012" strokeDasharray="0.04 0.03" />
      <text x="0.22" y="0.23" fontSize="0.08" fill="#0f172a">Bleed</text>
      <line x1="0.65" y1="0.1" x2="0.77" y2="0.1" stroke="#16a34a" strokeWidth="0.01" strokeDasharray="0.03 0.03" />
      <text x="0.81" y="0.13" fontSize="0.08" fill="#0f172a">Safe</text>
      <text x="0.65" y="0.23" fontSize="0.08" fill="#0f172a">{widthIn}" × {heightIn}"</text>
    </g>
  );
}
